import React from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Battery, Gauge, ShieldCheck, Users, Bolt, IdCard, Zap } from "lucide-react";
import { ScooterHelmet } from "./icons/ScooterHelmet";

const SCOOTER_IMG = "https://customer-assets.emergentagent.com/job_scooter-rental-book/artifacts/kidb633j_image.png";

export default function ScooterShowcase({ onBook }) {
  const { t } = useI18n();
  // Note: ABS + TCS is rendered as a special large card; the other 4 cards are below
  const otherSpecs = [
    { icon: Battery, k: t.showcase.specs.range, v: "100 km" },
    { icon: Gauge, k: t.showcase.specs.speed, v: "120 km/h" },
    { icon: Users, k: t.showcase.specs.riders, v: t.showcase.specs.riders_v },
    { icon: Bolt, k: t.showcase.specs.power, v: "9 900 W" },
    { icon: IdCard, k: t.showcase.specs.license, v: "B / A1" },
  ];
  return (
    <section id="fleet" className="relative py-24 sm:py-32 bg-[#050505]" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-scooter-yellow">{t.showcase.label}</div>
              <Zap className="w-3.5 h-3.5 text-scooter-yellow fill-scooter-yellow" />
              <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.showcase.zero_emissions || "Zero emissioni"}</span>
            </div>
            <h2 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">{t.showcase.title}</h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">{t.showcase.desc}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 relative aspect-[5/4] lg:aspect-auto lg:min-h-[600px] bg-[#0a0a0a] border border-white/10 overflow-hidden group">
            <img src={SCOOTER_IMG} alt="NIU NQiX 500 in the Dolomites" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-zinc-300">NIU</div>
                <div className="text-white text-3xl font-heading font-black tracking-tighter">NQiX 500</div>
              </div>
              <div className="bg-scooter-yellow text-black px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-1.5"><Zap className="w-3 h-3 fill-black" />100% Electric</div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-4">
            {/* Hero spec: ABS + TCS — large, no value text */}
            <div className="bg-[#0a0a0a] border border-white/10 p-8 hover:border-scooter-yellow/60 transition-colors" data-testid="spec-abs-tcs">
              <div className="flex items-start gap-4">
                <ShieldCheck className="w-7 h-7 text-scooter-yellow shrink-0" />
                <div>
                  <div className="text-white font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">{t.showcase.specs.abs_tcs}</div>
                  <div className="text-zinc-500 text-[11px] tracking-[0.3em] uppercase font-bold mt-2">{t.showcase.specs.security}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {otherSpecs.map((s, i) => (
                <div key={s.k} className="bg-[#0a0a0a] border border-white/10 p-5 hover:border-scooter-yellow/60 transition-colors" data-testid={`spec-${i}`}>
                  <s.icon className="w-5 h-5 text-scooter-yellow mb-3" />
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{s.k}</div>
                  <div className="text-white font-heading font-bold text-xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="bg-scooter-yellow text-black p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ScooterHelmet className="w-5 h-5" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold">{t.showcase.specs.helmet} · 18+</div>
                  <div className="font-heading font-bold text-xl">{t.showcase.specs.yes}</div>
                </div>
              </div>
              <button onClick={onBook} className="border-2 border-black px-4 h-10 text-xs font-bold tracking-[0.2em] uppercase hover:bg-black hover:text-scooter-yellow transition-colors" data-testid="showcase-book-btn">
                {t.nav.book}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
